/**
 * Search drawer opened from the header search icon.
 *
 * Self-contained on purpose: it queries the predictive search endpoint directly
 * instead of relying on `predictive-search.js`, so live results show up whether
 * or not the `predictive_search_enabled` theme setting renders the inline
 * predictive search UI elsewhere.
 */
class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.panel = this.querySelector('.search-drawer__panel');
    this.form = this.querySelector('.search-drawer__form');
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.results = this.querySelector('[data-search-drawer-results]');
    this.statusElement = this.querySelector('[data-search-drawer-status]');
    this.loadingText = this.getAttribute('data-loading-text');

    this.cachedResults = {};
    this.searchTerm = '';
    this.abortController = new AbortController();
    this.isOpen = false;

    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);

    this.querySelectorAll('[data-search-drawer-close]').forEach((element) => {
      element.addEventListener('click', () => this.close());
    });

    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));

    this.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.form.addEventListener('reset', this.onFormReset.bind(this));
    this.input.addEventListener('input', debounce(this.onInput.bind(this), 300));

    this.toggleResetButton();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
  }

  /* --------------------------------------------------------------------- */
  /* Open / close                                                          */
  /* --------------------------------------------------------------------- */

  onDocumentClick(event) {
    const toggle = event.target.closest('[data-search-drawer-toggle]');
    if (!toggle) return;

    event.preventDefault();
    this.open(toggle);
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    this.classList.add('active');
    document.body.classList.add('overflow-hidden');
    this.setToggleState(true);
    this.isOpen = true;

    // Flush the style change before focusing: a still-hidden input cannot take
    // focus. Staying inside the click handler keeps iOS opening the keyboard.
    this.getBoundingClientRect();
    trapFocus(this.panel, this.input);

    // On the search page the field opens prefilled with the current terms, so
    // fetch their results instead of waiting for the shopper to type again.
    const currentTerm = this.getQuery();
    if (currentTerm.length && !this.hasAttribute('results')) {
      this.searchTerm = currentTerm;
      this.getSearchResults(currentTerm);
    }
  }

  close() {
    if (!this.isOpen) return;

    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    this.setToggleState(false);
    this.isOpen = false;

    removeTrapFocus(this.activeElement);
  }

  setToggleState(expanded) {
    document.querySelectorAll('[data-search-drawer-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', expanded);
    });
  }

  /* --------------------------------------------------------------------- */
  /* Form                                                                  */
  /* --------------------------------------------------------------------- */

  getQuery() {
    return this.input.value.trim();
  }

  toggleResetButton() {
    if (!this.resetButton) return;
    this.resetButton.classList.toggle('hidden', this.input.value.length === 0);
  }

  onInput() {
    this.toggleResetButton();

    const newSearchTerm = this.getQuery();
    if (newSearchTerm === this.searchTerm) return;

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.clearResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    // An empty term would land on the search page with no query; a highlighted
    // result means the user is picking that link with the keyboard instead.
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    // Prevent the native reset, which would restore the value present on page load.
    event.preventDefault();

    this.input.value = '';
    this.searchTerm = '';
    this.abortController.abort();
    this.abortController = new AbortController();
    this.toggleResetButton();
    this.clearResults();
    this.input.focus();
  }

  onKeyup(event) {
    if (event.code === 'Escape') {
      this.close();
      return;
    }

    if (!this.hasAttribute('results')) return;

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Stop the caret from jumping to either end of the input while navigating results.
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  /* --------------------------------------------------------------------- */
  /* Results                                                               */
  /* --------------------------------------------------------------------- */

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    this.setAttribute('loading', 'true');
    this.setLiveRegionText(this.loadingText);

    // resources[limit] caps each group (suggestions, products, pages…) at 4 so
    // the drawer stays readable; the endpoint would otherwise return up to 10.
    const url =
      `${window.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}` +
      `&resources[limit]=4&section_id=predictive-search`;

    fetch(url, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.cachedResults[queryKey] = resultsMarkup;

        // A slower request for an older term must not overwrite the current one.
        if (this.getQuery() !== searchTerm) return;

        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        // 20 is AbortError, raised whenever a newer term supersedes this request.
        if (error?.code === 20 || error?.name === 'AbortError') return;

        this.removeAttribute('loading');
        this.clearResults();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    this.results.innerHTML = resultsMarkup;
    this.removeAttribute('loading');
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', 'true');

    // A new set of results always starts from the top of the scroll area.
    const body = this.querySelector('.search-drawer__body');
    if (body) body.scrollTop = 0;

    const liveRegionCount = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegionCount) this.setLiveRegionText(liveRegionCount.textContent);
  }

  clearResults() {
    this.results.innerHTML = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.setAttribute('aria-activedescendant', '');
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement || !statusText) return;

    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  /* --------------------------------------------------------------------- */
  /* Keyboard navigation through results                                   */
  /* --------------------------------------------------------------------- */

  switchOption(direction) {
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // `offsetParent` filters out the duplicated desktop/mobile page lists that
    // the predictive search section renders but hides at the current breakpoint.
    const allVisibleElements = Array.from(
      this.results.querySelectorAll('li, button.predictive-search__item')
    ).filter((element) => element.offsetParent !== null);

    if (!allVisibleElements.length) return;
    if (moveUp && !selectedElement) return;

    const selectedElementIndex = allVisibleElements.indexOf(selectedElement);
    let activeElementIndex = 0;

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex <= 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.results.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }
}

customElements.define('search-drawer', SearchDrawer);
